import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Box,
} from "@mui/material";
import {
  ContentCopy,
  Refresh,
  Visibility,
  VisibilityOff,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  adminAddUser,
  adminEditUser,
  disableUser,
  enableUser,
} from "../../api/admin";
import { Role, User } from "../../pages/playground/types";

interface UserDialogProps {
  open: boolean;
  user: User | null;
  onClose: (refresh: boolean) => void;
  onDisableEnable?: (userId: string, newDisabledState: boolean) => void;
  canManageUsers: boolean;
}

const initUserState = {
  id: undefined,
  email: "",
  name: "",
  surname: "",
  role: "USER" as Role,
  password: "",
};

/**
 * Dialog component for adding and editing users
 * Handles form inputs for all user fields including password
 */
export const UserDialog: React.FC<UserDialogProps> = ({
  open,
  user,
  onClose,
  onDisableEnable,
  canManageUsers,
}) => {
  // Local state for the user being edited
  const [editingUser, setEditingUser] = React.useState<User>(initUserState);
  const [showPassword, setShowPassword] = React.useState(false);
  const [copyMessage, setCopyMessage] = React.useState<string | null>(null);
  const [showDisableEnableConfirm, setShowDisableEnableConfirm] =
    React.useState(false);

  // Update local state when the user prop changes
  React.useEffect(() => {
    if (user) {
      setEditingUser({ ...user });
    } else {
      setEditingUser(initUserState);
    }
  }, [user]);

  // Generate a secure password
  const generatePassword = () => {
    const length = 16;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    handleFieldChange("password", password);
  };

  // Copy text to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(`${label} copied to clipboard`);
    } catch (err) {
      setCopyMessage(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  // Copy both email and password
  const copyBothCredentials = async () => {
    const email = editingUser?.email || "";
    const password = editingUser?.password || "";

    if (!email || !password) {
      setCopyMessage("Both email and password must be filled to copy");
      return;
    }

    const credentials = `Email: ${email}\nPassword: ${password}`;
    try {
      await navigator.clipboard.writeText(credentials);
      setCopyMessage("Email and password copied to clipboard");
    } catch (err) {
      setCopyMessage("Failed to copy credentials");
    }
  };

  // Handler for saving the user
  const handleSave = async () => {
    if (editingUser?.id) {
      await adminEditUser(editingUser);
    } else {
      await adminAddUser(editingUser);
    }

    setEditingUser(initUserState);
    onClose(true);
  };

  // Update a specific field in the user object
  const handleFieldChange = (field: keyof User, value: string | boolean) => {
    if (editingUser) {
      setEditingUser({ ...editingUser, [field]: value });
    }
  };

  // Handler for disabling/enabling user
  const handleDisableEnable = () => {
    setShowDisableEnableConfirm(true);
  };

  const confirmDisableEnable = async () => {
    if (!editingUser?.id) return;

    const action = editingUser.disabled ? "enable" : "disable";

    try {
      if (editingUser.disabled) {
        await enableUser(editingUser.id);
      } else {
        await disableUser(editingUser.id);
      }

      if (onDisableEnable) {
        onDisableEnable(editingUser.id, !editingUser.disabled);
      }
      setShowDisableEnableConfirm(false);
      onClose(true);
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      setCopyMessage(`Failed to ${action} user. Please try again.`);
      setShowDisableEnableConfirm(false);
    }
  };

  const isEditMode = editingUser && editingUser.id;
  const isAdminUser = editingUser?.role === "ADMIN";

  return (
    <>
    <Dialog
      open={open}
      onClose={() => {
        onClose(false);
        setEditingUser(initUserState);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{isEditMode ? "Edit user" : "New user"}</span>
          {isEditMode && editingUser?.disabled && (
            <Chip label="Disabled" size="small" color="error" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              value={editingUser?.email || ""}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy email">
                      <IconButton
                        onClick={() =>
                          copyToClipboard(editingUser?.email || "", "Email")
                        }
                        edge="end"
                        size="small"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              label="Password"
              value={editingUser?.password || ""}
              onChange={(e) => handleFieldChange("password", e.target.value)}
              helperText={
                editingUser?.id ? "Leave empty to keep current password" : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Generate password">
                      <IconButton
                        onClick={generatePassword}
                        edge="end"
                        size="small"
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy password">
                      <IconButton
                        onClick={() =>
                          copyToClipboard(
                            editingUser?.password || "",
                            "Password"
                          )
                        }
                        edge="end"
                        size="small"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Name"
              value={editingUser?.name || ""}
              onChange={(e) => handleFieldChange("name", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Surname"
              value={editingUser?.surname || ""}
              onChange={(e) => handleFieldChange("surname", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editingUser?.role || "USER"}
                label="Role"
                onChange={(e) => handleFieldChange("role", e.target.value)}
              >
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="MANAGER">Manager</MenuItem>
                <MenuItem value="COPYWRITER">Copywriter</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Box>
          {isEditMode && canManageUsers && onDisableEnable && (
            <Button
              onClick={handleDisableEnable}
              color={editingUser.disabled ? "success" : "error"}
              startIcon={
                editingUser.disabled ? <CheckCircleIcon /> : <BlockIcon />
              }
              disabled={isAdminUser}
            >
              {editingUser.disabled ? "Enable" : "Disable"}
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button
            onClick={copyBothCredentials}
            variant="outlined"
            startIcon={<ContentCopy />}
            disabled={!editingUser?.email || !editingUser?.password}
          >
            Copy Credentials
          </Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={!!copyMessage}
        autoHideDuration={3000}
        onClose={() => setCopyMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopyMessage(null)}
          severity="success"
          variant="filled"
        >
          {copyMessage}
        </Alert>
      </Snackbar>
    </Dialog>

    {/* Disable/Enable Confirmation Dialog */}
    <Dialog
      open={showDisableEnableConfirm}
      onClose={() => setShowDisableEnableConfirm(false)}
    >
      <DialogTitle>
        {editingUser?.disabled ? "Enable User" : "Disable User"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {editingUser?.disabled
            ? "Are you sure you want to enable this user?"
            : "Are you sure you want to disable this user? They will not be able to log in."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowDisableEnableConfirm(false)}>
          Cancel
        </Button>
        <Button
          onClick={confirmDisableEnable}
          color={editingUser?.disabled ? "success" : "error"}
          variant="contained"
        >
          {editingUser?.disabled ? "Enable" : "Disable"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};
